import type { UserRole } from "@/types/user.types";

// Role priority - higher index = higher priority
const ROLE_PRIORITY: UserRole[] = ["STUDENT", "ENTERPRISE_RECRUITER", "HR_STAFF", "ADMIN"];

/**
 * The builtin roles seeded by the backend (V5__seed_roles_permissions.sql).
 * The backend also supports admin-created CUSTOM roles, which have no frontend
 * equivalent — see {@link normalizeRoles} for how those are surfaced.
 */
const KNOWN_ROLES: ReadonlySet<string> = new Set<UserRole>(ROLE_PRIORITY);

/**
 * Type guard for a normalized role string.
 */
export function isKnownRole(role: string): role is UserRole {
  return KNOWN_ROLES.has(role);
}

/**
 * Normalize backend role format to frontend format
 *
 * Backend sends: "ROLE_STUDENT", "ROLE_HR_STAFF", "ROLE_ENTERPRISE_RECRUITER", "ROLE_ADMIN"
 * Frontend expects: "STUDENT", "HR_STAFF", "ENTERPRISE_RECRUITER", "ADMIN"
 *
 * Note: this performs an unchecked cast and is kept for callers that already know the
 * role is builtin. Prefer {@link normalizeRoles}, which validates.
 *
 * @param role - Role string from backend (may or may not have "ROLE_" prefix)
 * @returns Normalized role without "ROLE_" prefix
 */
export function normalizeRole(role: string): UserRole {
  return role.replace("ROLE_", "") as UserRole;
}

/**
 * Normalize an array of roles from backend format, keeping only roles the frontend
 * knows how to gate on. Duplicates are removed and order follows the input.
 *
 * Unrecognized roles (admin-created CUSTOM roles) are dropped and logged rather than
 * silently coerced: a user holding only custom roles would otherwise be treated as a
 * STUDENT and hit "Access Denied" on every staff route with nothing in the console to
 * explain why. Route access for custom roles requires permission-based guards.
 *
 * @param roles - Array of role strings from backend (may be null on an invalid session)
 * @returns Recognized roles, deduplicated
 */
export function normalizeRoles(roles: string[] | null | undefined): UserRole[] {
  const recognized: UserRole[] = [];

  for (const raw of roles ?? []) {
    const normalized = normalizeRole(raw);
    if (isKnownRole(normalized)) {
      if (!recognized.includes(normalized)) {
        recognized.push(normalized);
      }
    } else {
      console.error(
        `[auth] Unrecognized role "${raw}" from backend — ignoring. UI access is gated on ` +
          `builtin roles (${ROLE_PRIORITY.join(", ")}); custom roles are not yet supported.`
      );
    }
  }

  return recognized;
}

/**
 * Get the primary (highest priority) role from an array of roles.
 * Priority: ADMIN > HR_STAFF > ENTERPRISE_RECRUITER > STUDENT
 *
 * The primary role decides which dashboard the user lands on. It must NOT be used as the
 * sole basis for route access — a user holding both STUDENT and HR_STAFF has a primary
 * role of HR_STAFF and would be locked out of student routes. Use the full role list from
 * {@link normalizeRoles} for access checks.
 *
 * @param roles - Array of role strings from backend (may be null on an invalid session)
 * @returns The highest priority recognized role, defaulting to STUDENT
 */
export function getPrimaryRole(roles: string[] | null | undefined): UserRole {
  const normalizedRoles = normalizeRoles(roles);

  // Find the highest priority role
  for (let i = ROLE_PRIORITY.length - 1; i >= 0; i--) {
    const role = ROLE_PRIORITY[i];
    if (role && normalizedRoles.includes(role)) {
      return role;
    }
  }

  // No recognized role at all — the user is about to be treated as a student, which is
  // almost certainly wrong for staff accounts. Make it visible rather than silent.
  if (roles && roles.length > 0) {
    console.error(
      `[auth] No recognized role in [${roles.join(", ")}] — defaulting to STUDENT. ` +
        `Expected one of: ${ROLE_PRIORITY.join(", ")}.`
    );
  }

  return "STUDENT";
}
